'use client'

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react'

interface TurnstileWidgetProps {
  onVerify: (token: string) => void
  onError?: (error: any) => void
  onExpire?: () => void
  className?: string
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact'
  locale?: string
}

export interface TurnstileWidgetRef {
  reset: () => void
  getToken: () => string | undefined
}

const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  ({ onVerify, onError, onExpire, className, theme = 'auto', size = 'normal', locale }, ref) => {
    const turnstileRef = useRef<TurnstileInstance>(null)
    const tokenRef = useRef<string | undefined>(undefined)
    const [retryCount, setRetryCount] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const maxRetries = 3

    useImperativeHandle(ref, () => ({
      reset: () => {
        // Only reset the widget UI, not the token itself
        // This allows token reuse for session-based verification
        turnstileRef.current?.reset()
        // Preserve the token - let the parent component manage token lifecycle
        // tokenRef.current remains unchanged
        setRetryCount(0)
      },
      getToken: () => tokenRef.current
    }))

    const handleVerify = (token: string) => {
      console.log('Turnstile verified successfully')
      tokenRef.current = token
      setRetryCount(0)
      setIsLoading(false)
      onVerify(token)
    }

    const handleExpire = () => {
      console.log('Turnstile token expired')
      tokenRef.current = undefined
      onExpire?.()
    }

    const handleError = (error: any) => {
      console.error('Turnstile error:', error)
      setIsLoading(false)
      
      // Prevent infinite retries
      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1)
        console.log(`Turnstile retry attempt ${retryCount + 1} of ${maxRetries}`)
        
        // Reset and retry after a delay
        setTimeout(() => {
          turnstileRef.current?.reset()
        }, 2000 * (retryCount + 1)) // Exponential backoff
      } else {
        console.error('Turnstile max retries reached, stopping')
        onError?.(new Error('Maximum verification attempts reached. Please refresh the page.'))
      }
    }

    useEffect(() => {
      // Set a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        if (isLoading && !tokenRef.current) {
          console.warn('Turnstile loading timeout')
          setIsLoading(false)
        }
      }, 10000) // 10 second timeout

      return () => clearTimeout(timeout)
    }, [isLoading])

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

    if (!siteKey) {
      // Silently skip if not configured - this is expected in some environments
      return null
    }

    return (
      <div className={className}>
        {retryCount >= maxRetries ? (
          <div className="border border-red-300 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
            <p className="text-red-600 dark:text-red-400 text-sm">
              {locale === 'de' 
                ? 'Sicherheitsprüfung fehlgeschlagen. Bitte laden Sie die Seite neu.'
                : 'Security verification failed. Please refresh the page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm underline"
            >
              {locale === 'de' ? 'Seite neu laden' : 'Refresh page'}
            </button>
          </div>
        ) : (
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            onSuccess={handleVerify}
            onError={handleError}
            onExpire={handleExpire}
            options={{
              theme,
              size,
              language: locale === 'de' ? 'de' : 'en',
              action: 'submit-form',
              appearance: 'interaction-only', // Changed from 'always' to prevent auto-verify loops
              retry: 'never', // We handle retries manually
              refreshExpired: 'manual', // We handle expiry manually
              execution: 'render' // Explicit execution mode
            }}
            className="min-h-[65px]"
          />
        )}
      </div>
    )
  }
)

TurnstileWidget.displayName = 'TurnstileWidget'

export default TurnstileWidget